import { X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useSettings } from '../../lib/settings.tsx';
import { fromMetres, toMetres, unitLabel, unitName } from '../../lib/units.ts';

/* La distance à couvrir, saisie au chiffre près.
 *
 * ── Pourquoi un champ, et non le pavé du practice ───────────────────────────
 *
 * Au practice, la distance est une CIBLE qu'on se donne — « je travaille à
 * 70 m » — et le pavé de raccourcis y est le bon outil : les valeurs sont
 * rondes, connues d'avance, et l'on tape à côté d'un panneau qui les affiche.
 *
 * Ici elle est MESURÉE. Le télémètre rend 137, pas 130 : arrondir au raccourci
 * le plus proche, c'est jeter ce qu'on vient de mesurer — et sur un conseil de
 * club, sept mètres font changer de fer. On demande donc le nombre, et rien
 * d'autre.
 *
 * ── Ce qui est fait pour le gant ────────────────────────────────────────────
 *
 * Le champ prend le focus tout seul, le clavier numérique s'ouvre avec lui, et
 * le contenu est sélectionné : on tape les trois chiffres et on valide. La
 * touche Entrée valide aussi, pour n'avoir pas à viser un bouton après avoir
 * visé un clavier.
 *
 * ── Le champ garde sa propre chaîne ─────────────────────────────────────────
 *
 * Lié directement à un nombre, il ne pourrait jamais être vide : tout effacer
 * rendrait « 0 », et ce zéro se collerait devant la frappe suivante — on tape
 * 2 en voulant 20 et on lit « 02 ». C'est la même précaution que sur le pavé du
 * practice, et elle a été apprise là-bas.
 */
export function SaisieDistance({ initial, onValider, onFermer, onOublier }: {
    /** Point de départ EN MÈTRES. */
    initial: number;
    /** Reçoit des MÈTRES, quelle que soit l'unité affichée. */
    onValider: (distanceM: number) => void;
    onFermer: () => void;
    /** Proposé seulement quand une distance est déjà en vigueur. */
    onOublier?: () => void;
}) {
    const { unit } = useSettings();
    const [draft, setDraft] = useState(() => String(fromMetres(initial, unit)));
    const champ = useRef<HTMLInputElement>(null);

    useEffect(() => {
        champ.current?.focus();
        champ.current?.select();
    }, []);

    const valeur = Number(draft);
    const valide = draft.trim() !== '' && Number.isFinite(valeur) && valeur >= 1 && valeur <= 450;

    const valider = (): void => {
        if (!valide) return;
        onValider(toMetres(Math.round(valeur), unit));
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4"
            role="dialog" aria-modal="true" aria-label="Distance à couvrir">
            <div className="w-full max-w-sm rounded-box bg-base-100 p-4 text-base-content shadow-xl">
                <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                        <h2 className="font-display text-xl font-bold leading-tight">
                            Distance à couvrir
                        </h2>
                        <p className="mt-0.5 text-xs opacity-60">
                            En {unitName(unit)}, telle que vous l’avez mesurée.
                        </p>
                    </div>
                    <button type="button" onClick={onFermer} aria-label="Fermer"
                        className="btn btn-ghost btn-sm btn-square shrink-0">
                        <X size={18} aria-hidden="true" />
                    </button>
                </div>

                <form onSubmit={(e) => { e.preventDefault(); valider(); }} className="mt-3">
                    <div className="flex items-baseline justify-center gap-2">
                        <input
                            ref={champ}
                            type="text"
                            inputMode="numeric"
                            enterKeyHint="done"
                            aria-label={`Distance en ${unitName(unit)}`}
                            className="input input-bordered h-16 w-40 text-center font-score text-4xl tabular-nums"
                            value={draft}
                            onFocus={(e) => e.currentTarget.select()}
                            onChange={(e) => setDraft(e.target.value.replace(/[^\d]/g, ''))}
                        />
                        <span className="font-score text-2xl opacity-60">{unitLabel(unit)}</span>
                    </div>

                    {/* Le refus se dit AVANT d'appuyer, pas après : le bouton
                        reste inerte et la raison est écrite dessous. */}
                    {!valide && draft.trim() !== '' && (
                        <p className="mt-2 text-center text-xs" style={{ color: 'var(--brique-bord)' }}>
                            Entre 1 et 450 {unitLabel(unit)}.
                        </p>
                    )}

                    <button type="submit" disabled={!valide}
                        className="btn btn-primary mt-4 h-14 w-full text-lg font-bold">
                        Conseiller un club
                    </button>
                </form>

                {onOublier && (
                    <button type="button" onClick={onOublier}
                        className="btn btn-ghost btn-sm mt-2 w-full">
                        Oublier cette distance
                    </button>
                )}
            </div>
        </div>
    );
}
